import React, { useEffect, useMemo, useRef, useState } from "react";
import { SelectInputMulti, SelectInput, DialogBox, DialogBoxFooterType } from "~/core";
import { picklistNames, selectors as picklistSelectors } from "~/core/picklist";
import { PICKLIST_IRRIGATION_TYPE } from "~/core/picklist/picklist-names";

import { withMasked, withApiResult } from "~/hocs";
import { FieldAPI } from "@ai360/core";
import { IFormatter } from "@ai360/core";
import { messages } from "../../i18n-messages";
import {
    getBatchFieldDetails,
    getBatchFieldRemovalDetails,
    getDetailedFieldInfo,
} from "../../selectors";
import { getUser } from "~/login/selectors";
import { getAgvanceFieldClassificationList } from "../../../../selectors";
import {
    confirmBatchDetailsRemoval,
    updateBatchFieldDetails,
    updateBatchFieldRemovalDetails,
    saveBatchDetails,
} from "../../actions";
import { IrrigatedListOptions, YesNoOptions } from "../../../../sagas";
import { AgvanceUtils } from "~/admin/setup/customer/agvance-utils";
import { getTheUserPersonalityId } from "~/login/selectors";
import { IS_SELECTABLE } from "~/utils/products/use-product-list";
import {
    isCropUnlinked,
    toCropDropdownOptions,
    toCropSelectOptions,
    useCropList,
    useFieldsCropScope,
} from "~/utils/crops/use-crop-list";
import {
    ICropPurposeDropdownOption,
    isCropPurposeUnlinked,
} from "~/utils/crops/use-crop-purpose-list";
import { CropPurposeSelect } from "~/utils/crops/crop-purpose-select";
import { messages as globalMessages } from "~/i18n-messages";
import { useAppDispatch, useAppSelector } from "~/store/hooks";

import { RadioButton, RadioButtonGroup, Checkbox } from "~/core";
import { Loader, LoaderTypes } from "~/core";

import "./batch-edit-details.css";

interface IBatchEditDetailsProps {
    changeOrganicValue: (value: boolean) => void;
    fieldCount: number;
    intl: IFormatter;
    onClose: () => void;
    visible: boolean;
}

const BatchEditDetails_ = ({
    changeOrganicValue,
    fieldCount,
    intl,
    onClose,
    visible,
}: IBatchEditDetailsProps) => {
    const dispatch = useAppDispatch();

    const agvanceFieldClassificationList = useAppSelector(
        getAgvanceFieldClassificationList
    ) as FieldAPI.IAgvClassification[];
    const batchField = useAppSelector(getBatchFieldDetails);
    const batchFieldRemoval = useAppSelector(getBatchFieldRemovalDetails);
    const detailedFieldInfo = useAppSelector(getDetailedFieldInfo);
    const irrigationTypeOptions = useAppSelector((state) =>
        picklistSelectors.getPicklistOptionsFromCode(
            state,
            picklistNames.getPickListCode(PICKLIST_IRRIGATION_TYPE)
        )
    );
    const userInfo = useAppSelector(getUser);
    const personalityId = useAppSelector(getTheUserPersonalityId);

    const cropScope = useFieldsCropScope(
        (detailedFieldInfo as Partial<FieldAPI.IField>[]).map((field) => field.fieldGuid)
    );
    const { crops } = useCropList({ scope: cropScope });
    const cropData = useMemo(() => toCropDropdownOptions(crops), [crops]);

    const [cropPurposeData, setCropPurposeData] = useState<ICropPurposeDropdownOption[]>([]);

    const [selectedRadioButton, setSelectedRadioButton] = useState<string | boolean>("add");
    const [affectedFields, setAffectedFields] = useState<FieldAPI.IField[]>(null);
    const addMenuRef = useRef<HTMLDivElement>(null);
    const [menuHeight, setMenuHeight] = useState<number>(0);
    const { formatMessage } = intl;

    useEffect(() => {
        if (addMenuRef.current) {
            setMenuHeight(addMenuRef.current.offsetHeight);
        }
    }, [selectedRadioButton]);

    const onSaveBatchDetailsRemoval = () => {
        const batchFieldDetails: Partial<FieldAPI.IRemovalFieldTemplate> = batchFieldRemoval;
        const fieldDetails: Partial<FieldAPI.IField>[] = detailedFieldInfo;
        let filteredFields = [];

        const {
            clearClassifications,
            clearCrop,
            clearCropPurpose,
            clearIrrigation,
            clearIrrigationType,
        } = batchFieldDetails;

        if (
            !clearClassifications?.length &&
            !clearCrop &&
            !clearCropPurpose &&
            !clearIrrigation &&
            !clearIrrigationType
        ) {
            filteredFields = [];
        }

        filteredFields = fieldDetails.filter((field) => {
            if (clearCrop && field.cropGuid != null) return true;
            if (clearCropPurpose && field.cropPurposeGuid != null) return true;
            if (clearIrrigation && field.irrigated != null) return true;
            if (clearIrrigationType && field.irrigationTypeGuid != null) return true;
            if (
                clearClassifications?.length &&
                field.classifications?.some((c) => clearClassifications.includes(c))
            )
                return true;

            return false;
        });

        setAffectedFields(filteredFields);
    };

    const hasValue = (): boolean => {
        if (selectedRadioButton === "add") {
            const hasAnyValue =
                batchField.certifiedOrganic != null ||
                batchField.cropGuid != null ||
                batchField.cropPurposeGuid != null ||
                batchField.drainageTile != null ||
                batchField.irrigated != null ||
                batchField.irrigationTypeGuid != null ||
                batchField.lockBoundary != null ||
                batchField.lockBoundaryModifiedByGuid != null ||
                batchField.classifications?.length > 0;

            return hasAnyValue && !isSelectedCropUnlinked && !isSelectedCropPurposeUnlinked;
        } else {
            return (
                batchFieldRemoval.clearCrop == true ||
                batchFieldRemoval.clearCropPurpose == true ||
                batchFieldRemoval.clearIrrigation == true ||
                batchFieldRemoval.clearIrrigationType == true ||
                batchFieldRemoval.clearClassifications.length > 0
            );
        }
    };

    const clearData = (): void => {
        dispatch(
            updateBatchFieldDetails({
                certifiedOrganic: null,
                classifications: null,
                cropGuid: null,
                cropPurposeGuid: null,
                drainageTile: null,
                irrigated: null,
                irrigationTypeGuid: null,
                lockBoundary: null,
                lockBoundaryModifiedByGuid: null,
            })
        );
        dispatch(
            updateBatchFieldRemovalDetails({
                clearClassifications: [],
                clearCrop: false,
                clearCropPurpose: false,
                clearIrrigation: false,
                clearIrrigationType: false,
            })
        );
    };

    const handleClose = (): void => {
        clearData();
        onClose();
    };

    const handleSave = (): void => {
        if (selectedRadioButton === "add") {
            dispatch(saveBatchDetails());
            handleClose();
        } else {
            onSaveBatchDetailsRemoval();
        }
    };

    const handleDetailChange = (field: Partial<FieldAPI.IField>): void => {
        dispatch(updateBatchFieldDetails(field));
    };

    const handleRemovalDetailChange = (field: Partial<FieldAPI.IRemovalFieldTemplate>): void => {
        dispatch(updateBatchFieldRemovalDetails(field));
    };

    const handleIrrigatedChange = (value: number): void => {
        let irrigationTypeGuid = batchField.irrigationTypeGuid;
        if (value === 0 || value == null) {
            irrigationTypeGuid = null;
        }
        dispatch(updateBatchFieldDetails({ irrigated: value, irrigationTypeGuid }));
    };

    const classificationOptions = agvanceFieldClassificationList
        .map((c) => ({
            value: c.classificationKey,
            label: c.classificationName,
        }))
        .sort((a, b) => (b.label > a.label ? -1 : 1));

    const cropOptions = useMemo(
        () => toCropSelectOptions(cropData, formatMessage),
        [cropData, formatMessage]
    );
    const isSelectedCropUnlinked = isCropUnlinked(batchField.cropGuid, cropData);
    const cropNotLinkedText = isSelectedCropUnlinked
        ? formatMessage(globalMessages.cropNotLinkedInAgvance)
        : undefined;
    const isSelectedCropPurposeUnlinked = isCropPurposeUnlinked(
        batchField.cropPurposeGuid,
        cropPurposeData
    );

    const chooseWarningDialogMsg = (): string => {
        if (affectedFields === null) {
            return "";
        } else if (affectedFields.length === 0) {
            return formatMessage(messages.noAffectedFields);
        } else {
            return formatMessage(messages.removeVeifyDialogue, {
                properties: changingValuesToMessage(),
                fieldsCount: affectedFields.length.toString(),
                totalFields: fieldCount.toString(),
            });
        }
    };

    const changingValuesToMessage = (): string => {
        const parts: string[] = [
            batchFieldRemoval.clearCrop && formatMessage(messages.crop),
            batchFieldRemoval.clearCropPurpose && formatMessage(messages.cropPurpose),
            batchFieldRemoval.clearIrrigation && formatMessage(messages.irrigated),
            batchFieldRemoval.clearIrrigationType && formatMessage(messages.irrigationType),
            ...classificationOptions
                .filter((c) => batchFieldRemoval.clearClassifications?.includes(c.value))
                .map((c) => c.label),
        ].filter(Boolean);

        if (parts.length <= 1) return parts[0] ?? "";

        const last = parts.pop();
        return `${parts.join(", ")}, and ${last}`;
    };

    const saveOption = (
        <div ref={addMenuRef} style={{ minHeight: menuHeight }}>
            <SelectInput
                key={`organic${batchField.certifiedOrganic}`}
                options={YesNoOptions}
                placeholderText={formatMessage(messages.organic)}
                onChange={(value) => changeOrganicValue(value)}
                value={batchField.certifiedOrganic}
                testId="batch-edit-organic-select"
            />
            <SelectInput
                key={`crop${batchField.cropGuid}`}
                options={cropOptions}
                optionIsHiddenKey={IS_SELECTABLE}
                placeholderText={formatMessage(messages.crop)}
                onChange={(cropGuid) => handleDetailChange({ cropGuid })}
                value={batchField.cropGuid}
                hasError={isSelectedCropUnlinked}
                toolTip={cropNotLinkedText}
                testId="batch-edit-crop-select"
            />
            <CropPurposeSelect
                key={`cropPurpose${batchField.cropPurposeGuid}`}
                cropGuid={batchField.cropGuid}
                cropScope={cropScope}
                emptyValue={null}
                errorStyle="hasError"
                formatMessage={formatMessage}
                onChange={(cropPurposeGuid) => handleDetailChange({ cropPurposeGuid })}
                onOptionsChange={setCropPurposeData}
                placeholderText={formatMessage(messages.cropPurpose)}
                testId="batch-edit-crop-purpose-select"
                value={batchField.cropPurposeGuid}
            />
            <SelectInput
                key={`drainage${batchField.drainageTile}`}
                options={YesNoOptions}
                placeholderText={formatMessage(messages.drainageTile)}
                onChange={(value) => handleDetailChange({ drainageTile: value })}
                value={batchField.drainageTile}
                testId="batch-edit-drainage-tile-select"
            />
            {classificationOptions?.length > 0 ? (
                <SelectInputMulti
                    key={`classificationMultiSelect`}
                    isClearable={true}
                    options={classificationOptions}
                    placeholder={formatMessage(messages.classification)}
                    onChange={(classificationValues) => {
                        const classifications = agvanceFieldClassificationList.filter((co) =>
                            classificationValues.includes(co.classificationKey)
                        );
                        handleDetailChange({ classifications });
                    }}
                    showTopLabel={true}
                    sort={true}
                    value={classificationOptions.filter((c) =>
                        batchField.classifications
                            ?.map((b) => b.classificationKey)
                            .includes(c.value)
                    )}
                />
            ) : null}
            <SelectInput
                key={`irrigation_${batchField.irrigated}`}
                options={IrrigatedListOptions}
                placeholderText={formatMessage(messages.irrigated)}
                onChange={(irrigated) => handleIrrigatedChange(irrigated)}
                value={batchField.irrigated}
                testId="batch-edit-irrigated-select"
            />
            <SelectInput
                disabled={batchField.irrigated === 0 || batchField.irrigated == null}
                key={`irrigationType_${batchField.irrigationTypeGuid}`}
                options={irrigationTypeOptions}
                placeholderText={formatMessage(messages.irrigationType)}
                onChange={(irrigationTypeGuid) => handleDetailChange({ irrigationTypeGuid })}
                value={batchField.irrigationTypeGuid}
                testId="batch-edit-irrigation-type-select"
            />
            {userInfo.role.lockBoundary ? (
                <SelectInput
                    key={`lockBoundary${batchField.lockBoundary}`}
                    options={YesNoOptions}
                    placeholderText={formatMessage(messages.lockBoundary)}
                    onChange={(value) =>
                        handleDetailChange({
                            lockBoundary: value,
                            lockBoundaryModifiedByGuid: userInfo.userGuid,
                        })
                    }
                    value={batchField.lockBoundary}
                    testId="batch-edit-lock-boundary-select"
                />
            ) : null}
        </div>
    );

    const usedClassificationValues = new Set(
        detailedFieldInfo.flatMap((f) => f.classifications as number[])
    );
    const filteredOptions = classificationOptions.filter((option) =>
        usedClassificationValues.has(option.value)
    );
    const isAgvanceConnected = () => {
        return AgvanceUtils.isAgvanceConnected(personalityId);
    };

    const removeOption = (
        <div ref={addMenuRef} style={{ minHeight: menuHeight }}>
            {usedClassificationValues.size > 0 && classificationOptions?.length > 0 ? (
                <div id={`classificationRemoveMultiSelect`}>
                    <SelectInputMulti
                        key={`classificationRemoveMultiSelect`}
                        isClearable={true}
                        options={filteredOptions}
                        placeholder={formatMessage(messages.classification)}
                        onChange={(classificationValues) => {
                            handleRemovalDetailChange({
                                clearClassifications: classificationValues,
                            });
                        }}
                        showTopLabel={true}
                        sort={true}
                        value={classificationOptions.filter((c) =>
                            batchFieldRemoval.clearClassifications?.map((b) => b).includes(c.value)
                        )}
                    />
                </div>
            ) : null}
            <div className="remove-option" id={`classificationRemoveCrop`}>
                <span className="remove-option-title">{formatMessage(messages.crop)}</span>
                <Checkbox
                    label={formatMessage(messages.clearAll)}
                    value={batchFieldRemoval.clearCrop}
                    onChange={(e, value) => {
                        handleRemovalDetailChange({ clearCrop: value });
                        handleRemovalDetailChange({ clearCropPurpose: value });
                    }}
                    testId="batch-edit-clear-crop-checkbox"
                />
            </div>
            <div className="remove-option remove-subset" id={`classificationRemoveCropPurpose`}>
                <span className="remove-option-title">{formatMessage(messages.cropPurpose)}</span>
                <Checkbox
                    label={formatMessage(messages.clearAll)}
                    value={batchFieldRemoval.clearCropPurpose}
                    onChange={(e, value) => {
                        handleRemovalDetailChange({
                            clearCropPurpose: value || batchFieldRemoval.clearCrop,
                        });
                    }}
                    testId="batch-edit-clear-crop-purpose-checkbox"
                />
            </div>
            <div className="remove-option" id={`classificationRemoveIrrigated`}>
                <span className="remove-option-title">{formatMessage(messages.irrigated)}</span>
                <Checkbox
                    label={formatMessage(messages.clearAll)}
                    value={batchFieldRemoval.clearIrrigation}
                    onChange={(e, value) => {
                        handleRemovalDetailChange({ clearIrrigation: value });
                        handleRemovalDetailChange({ clearIrrigationType: value });
                    }}
                    testId="batch-edit-clear-irrigation-checkbox"
                />
            </div>
            <div className="remove-option remove-subset" id={`classificationRemoveIrrigationType`}>
                <span className="remove-option-title">
                    {formatMessage(messages.irrigationType)}
                </span>
                <Checkbox
                    label={formatMessage(messages.clearAll)}
                    value={batchFieldRemoval.clearIrrigationType}
                    onChange={(e, value) => {
                        handleRemovalDetailChange({
                            clearIrrigationType: value || batchFieldRemoval.clearIrrigation,
                        });
                    }}
                    testId="batch-edit-clear-irrigation-type-checkbox"
                />
            </div>
        </div>
    );

    return (
        <div id="batchEdit">
            <DialogBox
                action={
                    selectedRadioButton === "add"
                        ? formatMessage(messages.add)
                        : formatMessage(messages.remove)
                }
                actionDisabled={!hasValue()}
                isModal={true}
                closeOnEscape={true}
                closeOnClickOff={false}
                hideCloseX={false}
                footerType={DialogBoxFooterType.ACTION_CANCEL}
                unrestricted={true}
                draggable={true}
                title={formatMessage(messages.batchEditDetails, { fieldCount })}
                className="batch-edit-details blownup"
                onAction={handleSave}
                onClose={handleClose}
                isOpen={visible}
                testId="batch-edit-details-modal"
            >
                {!(classificationOptions?.length > 0) && isAgvanceConnected() ? (
                    <Loader
                        className="modal-loading"
                        type={LoaderTypes.LINE_SCALE_PULSE_OUT_RAPID}
                    />
                ) : null}

                <RadioButtonGroup
                    className="radio-grp"
                    selectedValue={selectedRadioButton}
                    afterOnChange={(val) => setSelectedRadioButton(val)}
                >
                    <RadioButton
                        value={"add"}
                        label={formatMessage(messages.add)}
                        testId="batch-edit-add-radio"
                    />
                    <RadioButton
                        value={"remove"}
                        label={formatMessage(messages.remove)}
                        testId="batch-edit-remove-radio"
                    />
                </RadioButtonGroup>
                {selectedRadioButton === "add" ? saveOption : removeOption}
            </DialogBox>
            <DialogBox
                isOpen={affectedFields !== null}
                onAction={() => {
                    setAffectedFields(null);
                    dispatch(confirmBatchDetailsRemoval());
                    handleClose();
                }}
                onClose={() => {
                    setAffectedFields(null);
                }}
                footerType={
                    affectedFields !== null && affectedFields.length == 0
                        ? DialogBoxFooterType.OK
                        : DialogBoxFooterType.YES_NO
                }
                title={formatMessage(messages.removeVeifyTitle)}
                testId="batch-edit-affected-fields-modal"
            >
                {chooseWarningDialogMsg()}
            </DialogBox>
        </div>
    );
};

export const BatchEditDetails: React.FC<Omit<IBatchEditDetailsProps, "intl">> = withMasked(
    withApiResult(BatchEditDetails_)
);
